import { useState, useEffect } from "react";
import { X, Plus, Minus } from "lucide-react";
import { message } from "antd";
import foodApiService from "../../../services/foodApiService";
import { cn } from "../../../lib/utils";
import {
  gluceraFieldHint,
  gluceraFieldLabel,
  gluceraFormControl,
  gluceraFormMetric,
  gluceraSheetButtonPrimary,
  gluceraSheetButtonSecondary,
  gluceraSheetTitle,
} from "../../../utils/gluceraTypography";

interface AddFoodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newItem: any) => void;
  uploadedImage: string | null;
}

const AddFoodModal: React.FC<AddFoodModalProps> = ({
  isOpen,
  onClose,
  onSave,
  uploadedImage,
}) => {
  const [formData, setFormData] = useState({
    foodName: "",
    servingType: "",
    portionSize: "",
    numberOfServings: 1,
  });

  const [isLoading, setIsLoading] = useState(false);

  const servingTypes = ["Bowl", "Cup", "Glass", "Plate", "Piece", "Slice", "Scoop", "Handful", "Can", "Bottle"];
  const portionSizes = ["Small", "Medium", "Large"];

  useEffect(() => {
    if (isOpen) {
      setFormData({
        foodName: "",
        servingType: "",
        portionSize: "",
        numberOfServings: 1,
      });
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!formData.foodName.trim()) {
      message.error("Please enter food name");
      return;
    }
    if (!formData.servingType) {
      message.error("Please select serving type");
      return;
    }
    if (!formData.portionSize) {
      message.error("Please select portion size");
      return;
    }

    setIsLoading(true);

    try {
      const apiPayload = {
        name: formData.foodName.trim(),
        container: formData.servingType.toLowerCase(),
        size: formData.portionSize.toLowerCase(),
        count: formData.numberOfServings,
      };

      const response = await foodApiService.addManualFood(apiPayload);
      const apiData: any = (response.data as any)?.item ?? (response.data as any)?.data ?? response.data;

      const apiCount = apiData?.count ?? formData.numberOfServings;
      const unitLabel =
        apiData?.unit_label ?? formData.servingType.toLowerCase();
      const newFoodItem = {
        id: Date.now(),
        food_item_id: apiData?.food_item_id,
        name: apiData?.name ?? formData.foodName.trim(),
        csv_name: apiData?.csv_name,
        source: apiData?.source,
        count: apiCount,
        predicted_count: apiCount,
        predicted_serving_size_g: apiData?.serving_size_g ?? 0,
        predicted_serving_description: apiData?.serving_description,
        unit_label: unitLabel,
        predicted_unit_label: unitLabel,
        servings: apiCount,
        serving_description: apiData?.serving_description ?? `${formData.numberOfServings} ${formData.servingType.toLowerCase()} (${formData.portionSize.toLowerCase()})`,
        calories: apiData?.calorie_content_kcal ?? 0,
        calorie_content_kcal: apiData?.calorie_content_kcal ?? 0,
        weight: apiData?.serving_size_g ?? 0,
        protein_content_g: apiData?.protein_content_g ?? 0,
        carbohydrate_content_g: apiData?.carbohydrate_content_g ?? 0,
        net_carbs_g: apiData?.net_carbs_g ?? 0,
        fat_content_g: apiData?.fat_content_g ?? 0,
        sugar_g: apiData?.sugar_g ?? 0,
        dietary_fiber_g: apiData?.dietary_fiber_g ?? 0,
        trans_fat_g: apiData?.trans_fat_g ?? 0,
        saturated_fat_g: apiData?.saturated_fat_g ?? 0,
        GI_estimate: apiData?.GI_estimate ?? 0,
        GL_estimate: apiData?.GL_estimate ?? 0,
        image: uploadedImage || "/assets/imagedosa.png",
      };

      onSave(newFoodItem);
      onClose();
    } catch (error: any) {
      message.error(error.message || "Failed to add food item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecrement = () => {
    setFormData(prev => ({
      ...prev,
      numberOfServings: Math.max(1, prev.numberOfServings - 1),
    }));
  };

  const handleIncrement = () => {
    setFormData(prev => ({
      ...prev,
      numberOfServings: Math.min(20, prev.numberOfServings + 1),
    }));
  };

  if (!isOpen) return null;

  const selectChevron = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: "right 12px center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "20px",
  } as const;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[400px] rounded-t-2xl bg-card shadow-xl">
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-border" />
        </div>

        <div className="max-h-[85dvh] overflow-y-auto px-4 pb-[env(safe-area-inset-bottom,16px)]" style={{paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)'}}>
          <div className="mb-4 flex items-center justify-between py-2">
            <h2 className={gluceraSheetTitle}>Add Food Item</h2>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 transition-colors hover:bg-muted"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          <div className="mb-4 space-y-1.5">
            <label className={gluceraFieldLabel}>
              Food Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.foodName}
              onChange={e => {
                const val = e.target.value.replace(/[^\p{L}\p{N}\s\-'.,()&/]/gu, "");
                setFormData({ ...formData, foodName: val });
              }}
              className={gluceraFormControl}
              placeholder="Enter food name"
              maxLength={100}
            />
          </div>

          <div className="mb-4 space-y-1.5">
            <label className={gluceraFieldLabel}>
              Serving Type <span className="text-destructive">*</span>
            </label>
            <select
              value={formData.servingType}
              onChange={e =>
                setFormData({ ...formData, servingType: e.target.value })
              }
              className={cn(gluceraFormControl, "appearance-none")}
              style={selectChevron}
            >
              <option value="" disabled>
                Select serving type
              </option>
              {servingTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4 space-y-1.5">
            <label className={gluceraFieldLabel}>
              Portion Size <span className="text-destructive">*</span>
            </label>
            <select
              value={formData.portionSize}
              onChange={e =>
                setFormData({ ...formData, portionSize: e.target.value })
              }
              className={cn(gluceraFormControl, "appearance-none")}
              style={selectChevron}
            >
              <option value="" disabled>
                Select portion size
              </option>
              {portionSizes.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-5 space-y-1.5">
            <label className={gluceraFieldLabel}>
              Number of Servings
            </label>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <button
                type="button"
                onClick={handleDecrement}
                disabled={formData.numberOfServings <= 1}
                className="flex h-7 w-7 items-center justify-center rounded-md bg-card shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Minus className="h-4 w-4 text-muted-foreground" />
              </button>
              <span className={cn("flex-1 text-center", gluceraFormMetric)}>
                {formData.numberOfServings}
              </span>
              <button
                type="button"
                onClick={handleIncrement}
                disabled={formData.numberOfServings >= 20}
                className="flex h-7 w-7 items-center justify-center rounded-md bg-card shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <p className={cn(gluceraFieldHint, "text-muted-foreground/70")}>Adjust servings (1 to 20)</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className={cn("w-full rounded-lg bg-gradient-primary py-3 text-primary-foreground shadow-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50", gluceraSheetButtonPrimary)}
            >
              {isLoading ? "Adding..." : "Add Food"}
            </button>
            <button
              onClick={onClose}
              className={cn("w-full rounded-lg border border-border bg-card py-3 text-foreground transition-colors hover:bg-muted", gluceraSheetButtonSecondary)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFoodModal;
